import { useState } from 'react'
import { createPortal } from 'react-dom'
import Icon from '../components/Icon'
import { errorText, getLocale } from '../lib/i18n'
import { post } from '../lib/api'
import { itemPrice } from './api'
import type { Item } from '../../shared/market/model'

/**
 * "Make an offer" on an ad whose seller welcomes offers. It is only a way to start the conversation with the amount already in it:
 * the offer goes to the seller as an ordinary message, and the seller answers in the thread. No money moves.
 */
const L = (en: string, es: string) => (getLocale() === 'en' ? en : es)
/** Digits and one point, at most two decimals, at most $99999 (the same shape a price can have). */
const cleanAmount = (v: string) => { const t = v.replace(',', '.').replace(/[^\d.]/g, ''); const i = t.indexOf('.'); const whole = (i < 0 ? t : t.slice(0, i)).replace(/^0+(?=\d)/, '').slice(0, 5); return i < 0 ? whole : `${whole || '0'}.${t.slice(i + 1).replace(/\./g, '').slice(0, 2)}` }

export default function OfferDialog({ item, onClose }: { item: Item; onClose: () => void }) {
  const [amount, setAmount] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const valid = /^\d+(\.\d{1,2})?$/.test(amount) && Number(amount) > 0
  const send = async () => {
    if (!valid || busy) return
    setBusy(true); setError('')
    try {
      const { threadId } = await post<{ threadId: string }>(`/api/market/items/${item.id}/conversation`, {})
      const dollars = `$${Number(amount).toFixed(Number(amount) % 1 ? 2 : 0)}`
      const body = L(`Hi! I would like to offer ${dollars} for "${item.title}". Does that work for you?`, `¡Hola! Quisiera ofrecer ${dollars} por "${item.title}". ¿Te funciona?`)
      await post(`/api/market/threads/${threadId}`, { clientId: crypto.randomUUID(), body, offerCents: Math.round(Number(amount) * 100) })
      location.hash = `/thread/${threadId}`
    } catch (e) { setError(errorText(e)); setBusy(false) }
  }
  return createPortal(<div className="fb-layer" role="presentation">
    <form className="fb-sheet" role="dialog" aria-modal="true" aria-labelledby="offer-title" onSubmit={e => { e.preventDefault(); void send() }}>
      <h2 id="offer-title">{L('Make an offer', 'Hacer una oferta')}</h2>
      <p>{L(`The asking price is ${itemPrice(item, 'en-US')}. Your offer goes to the seller as a message, and they answer you there.`, `El precio pedido es ${itemPrice(item, 'es-PR')}. Tu oferta le llega al vendedor como mensaje y te responde ahí.`)}</p>
      <label>{L('Your offer', 'Tu oferta')}
        <span className="money-field"><span aria-hidden="true">$</span><input type="text" inputMode="decimal" autoComplete="off" placeholder="0.00" maxLength={8} autoFocus value={amount} onChange={e => setAmount(cleanAmount(e.target.value))}/></span>
      </label>
      {error && <p role="alert" className="error">{error}</p>}
      <div className="fb-actions">
        <button type="submit" className="fb-primary" disabled={!valid || busy}><Icon name="handshake" size={18}/>{busy ? '…' : L('Send offer', 'Enviar oferta')}</button>
        <button type="button" disabled={busy} onClick={onClose}>{L('Not now', 'Ahora no')}</button>
      </div>
    </form>
  </div>, document.body)
}
